import { Box, Tooltip, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { createElement } from 'react'
import {
  DateField,
  Filter,
  List,
  RecordRepresentation,
  ReferenceField,
  useCreatePath,
  useGetResourceLabel,
  useRecordContext,
  useResourceContext,
} from 'react-admin'

import config from './config'
import eventsExporter from './exporter'
import { AdvancedDatesFilter } from '../../components/AdvancedDatesFilter'
import AdvancedEnumTextField from '../../components/AdvancedEnumTextField'
import AdvancedTextField from '../../components/AdvancedTextField'
import CompactList from '../../components/CompactList'
import { EnumSelectInput } from '../../components/EnumSelectInput'
import ReferenceValue from '../../components/ReferenceValue'
import StaticText from '../../components/StaticText'
import {
  EVENT_ACTION_SUSPEND_USER_ACCOUNT,
  EVENT_ACTION_UNSUSPEND_USER_ACCOUNT,
  EVENT_ACTION_UPDATE_USER_ACCOUNT_SUSPENSION,
  EVENT_ACTIONS,
  EVENT_CHANNEL_GRAPHQL,
  EVENT_CHANNEL_ICONS,
  EVENT_CHANNEL_OPS,
  EVENT_CHANNEL_WEBHOOK,
  EVENT_CHANNELS,
} from '../../config/events'
import { SYSTEM_PERMISSION_READ } from '../../config/permissions'
import { useResourcePermissions } from '../../domain/permissions'
import { useSmallScreen } from '../../utils/theme'
import { getGroupPath } from '../accessGroups/routes'
import { AccountShowPage } from '../accounts/show'
import { GenericListLayout, ListActions, ListDatagrid } from '../common/list'
import { getResourceByName } from '../index'
import { formatCalendarDate } from '../users/suspension'

const useGetTargetLink = () => {
  const createPath = useCreatePath()
  return (record, reference) => {
    if (!record || !reference) return null
    if (['user-groups', 'vehicle-groups'].includes(reference)) {
      return getGroupPath(reference, record.id)
    }
    let link = createPath({ resource: reference, id: record.id, type: 'show' })
    const referenceConfig = getResourceByName(reference)
    const showResource = referenceConfig.options?.showResource
    if (reference === 'accounts') {
      link = AccountShowPage.path
    } else if (showResource) {
      const showResourceConfig = getResourceByName(showResource)
      const showResourceReferenceKey = showResourceConfig.options.referenceKey
      if (showResourceReferenceKey && record[showResourceReferenceKey]) {
        link = createPath({ resource: showResource, id: record[showResourceReferenceKey], type: 'show' })
      }
    }
    return link
  }
}

const ChannelFilter = () => <EnumSelectInput source="channel" options={EVENT_CHANNELS} />
const ActionFilter = () => {
  const translate = useTranslate()
  return (
    <EnumSelectInput
      source="action"
      options={EVENT_ACTIONS}
      translateArgs={{ resource: translate('resources.events.fields.entity_id') }}
    />
  )
}

const EventsFilters = (props) => (
  <Filter {...props}>
    <AdvancedDatesFilter source={['date_gte', 'date_lte']} alwaysOn />
    <ActionFilter alwaysOn />
    <ChannelFilter alwaysOn />
  </Filter>
)

export const EventsListLayout = ({ excludedFields = [], ...props }) => {
  const getTargetLink = useGetTargetLink()
  const translate = useTranslate()

  const isAuthorIncluded = !excludedFields.includes('author')
  const isChannelIncluded = !excludedFields.includes('channel')
  const isTargetIncluded = !excludedFields.includes('entity_id')

  return (
    <GenericListLayout
      compactListLayout={
        <CompactList
          icon={(record) => {
            const recordConfig = getResourceByName(record.resource)
            return <recordConfig.icon />
          }}
          title={() => <EventActionField source="action" />}
          body={() => (
            <>
              <StaticText>{translate('resources.events.fields.date')} </StaticText>
              <DateField source="date" showTime />
              {isAuthorIncluded && (
                <>
                  <br />
                  <StaticText>{translate('resources.events.fields.author')} </StaticText>
                  <EventAuthorField source="author" />
                </>
              )}
              {isChannelIncluded && (
                <>
                  <br />
                  <StaticText>{translate('resources.events.fields.channel')}: </StaticText>
                  <AdvancedEnumTextField source="channel" />
                </>
              )}
            </>
          )}
          referenceLink={
            isTargetIncluded
              ? {
                  getLink: (referenceRecord, reference) => getTargetLink(referenceRecord, reference),
                  getReference: (record) => record.resource,
                  source: 'entity_id',
                }
              : null
          }
        />
      }
      regularListLayout={
        <ListDatagrid rowClick={false}>
          <DateField source="date" showTime />
          <EventActionField source="action" />
          {isTargetIncluded && <EventTargetField source="entity_id" />}
          {isAuthorIncluded && <EventAuthorField sortable={false} source="author" />}
          {isChannelIncluded && <EventChannelField source="channel" />}
        </ListDatagrid>
      }
      {...props}
    />
  )
}

const EventActionField = ({ source }) => {
  const record = useRecordContext()
  const getResourceLabel = useGetResourceLabel()
  return (
    <>
      <AdvancedEnumTextField source={source} translateArgs={{ resource: getResourceLabel(record.resource, 1) }} />
      <EventSuspensionField />
      <UserGroupChangesField />
    </>
  )
}

const UserGroupChangesField = () => {
  const record = useRecordContext()
  const translate = useTranslate()
  const [canReadUsers] = useResourcePermissions('users', SYSTEM_PERMISSION_READ)
  const [canReadVehicleGroups] = useResourcePermissions('vehicle-groups', SYSTEM_PERMISSION_READ)
  if (record.resource !== 'user-groups' || !record.group_changes) return null

  return (
    <Box data-testid="user-group-changes">
      {[
        ['user_ids', 'users', canReadUsers],
        ['vehicle_group_ids', 'vehicle-groups', canReadVehicleGroups],
      ].map(([field, resource, canRead]) =>
        canRead
          ? ['added', 'removed'].flatMap((action) =>
              (record.group_changes[field]?.[action] || []).map((id) => (
                <Typography variant="body2" color="textSecondary" key={`${field}-${action}-${id}`}>
                  {translate(`resources.events.groupChanges.${field}.${action}`)}{' '}
                  <ReferenceField reference={resource} record={{ id }} source="id" link={false} emptyText={id}>
                    <RecordRepresentation />
                  </ReferenceField>
                </Typography>
              )),
            )
          : null,
      )}
    </Box>
  )
}

const useSuspensionEventDescription = (record) => {
  const translate = useTranslate()
  const { suspension } = record ?? {}
  if (!suspension) {
    return null
  }
  const t = (key, args) => translate(`resources.events.suspension.${key}`, args)
  const formatEnd = (date) => (date ? t('endOfDay', { date: formatCalendarDate(date) }) : t('indefinitely'))
  switch (record.action?.toUpperCase()) {
    case EVENT_ACTION_SUSPEND_USER_ACCOUNT:
      return suspension.suspended_until
        ? t('from', { start: formatCalendarDate(record.date), end: formatEnd(suspension.suspended_until) })
        : t('fromIndefinitely', { start: formatCalendarDate(record.date) })
    case EVENT_ACTION_UPDATE_USER_ACCOUNT_SUSPENSION:
      return t('changed', {
        previous: formatEnd(suspension.previous_suspended_until),
        next: formatEnd(suspension.suspended_until),
      })
    case EVENT_ACTION_UNSUSPEND_USER_ACCOUNT:
      return suspension.previous_suspended_until
        ? t('wasUntil', { end: formatEnd(suspension.previous_suspended_until) })
        : t('wasIndefinitely')
    default:
      return null
  }
}

const EventSuspensionField = () => {
  const record = useRecordContext()
  const translate = useTranslate()
  const description = useSuspensionEventDescription(record)
  if (!description) {
    return null
  }
  return (
    <>
      <Typography variant="body2" color="textSecondary" data-testid="event-suspension">
        {description}
      </Typography>
      {record.suspension.reason && (
        <Typography variant="body2" color="textSecondary">
          {translate('resources.events.suspension.reason', { reason: record.suspension.reason })}
        </Typography>
      )}
    </>
  )
}

const ResourceIconField = (props) => {
  const resource = useResourceContext(props)
  const resourceConfig = getResourceByName(resource)
  const getResourceLabel = useGetResourceLabel()
  return (
    <Tooltip title={getResourceLabel(resource)} placement="left">
      {createElement(resourceConfig.icon, { sx: { marginRight: 1 } })}
    </Tooltip>
  )
}

const EventTargetField = ({ source }) => {
  const getTargetLink = useGetTargetLink()
  const record = useRecordContext()
  return (
    <ReferenceField reference={record.resource} source={source} link={getTargetLink} sx={{ display: 'flex' }}>
      <Box display="flex" alignItems="center">
        <ResourceIconField />
        <RecordRepresentation />
      </Box>
    </ReferenceField>
  )
}

export const EventChannelField = ({ source, ...props }) => (
  <AdvancedEnumTextField source={source} icons={EVENT_CHANNEL_ICONS} {...props} />
)

export const EventAuthorField = () => {
  const record = useRecordContext()
  const isSmallScreen = useSmallScreen()
  if (![EVENT_CHANNEL_OPS, EVENT_CHANNEL_GRAPHQL, EVENT_CHANNEL_WEBHOOK].includes(record.channel)) {
    return <AdvancedTextField source="author.fullName" />
  }
  const resource = record.channel === EVENT_CHANNEL_OPS ? 'ops-users' : 'users'
  const commonProps = { reference: resource, source: 'author.id' }
  return isSmallScreen ? <ReferenceValue {...commonProps} /> : <ReferenceField link="show" {...commonProps} />
}

export default () => (
  <List
    empty={false}
    sort={config.options.defaultSort}
    filters={<EventsFilters />}
    actions={<ListActions hasExport />}
    exporter={eventsExporter}
  >
    <EventsListLayout />
  </List>
)
